import React, { useState, useEffect } from 'react';
import { User, CreateUserData, UpdateUserData } from '../../../types/user';
import { UserPermissions, DEFAULT_PERMISSIONS, ADMIN_PERMISSIONS, CONFERENTE_PERMISSIONS } from '../../../types/permissions';
import UserService from '../../../services/users';
import { listarModulosEmpresa } from '../../../services/conferencia';
import { listarClientes } from '../../../services/clientes';
import PermissionsEditor from './PermissionsEditor';
import ModalPortal from '../../../components/Common/ModalPortal';
import './UserFormModal.css';

interface UserFormModalProps {
  mode: 'create' | 'edit';
  user: User | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const UserFormModal: React.FC<UserFormModalProps> = ({ mode, user, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'permissions'>('info');

  // Dados do formulário
  const [formData, setFormData] = useState<{
    nome: string;
    email: string;
    telefone: string;
    cargo: string;
    tipo: 'admin' | 'supervisor' | 'operador' | 'conferente';
    ativo: boolean;
    gerar_senha_temporaria: boolean;
    enviar_email_configuracao: boolean;
  }>({
    nome: '',
    email: '',
    telefone: '',
    cargo: '',
    tipo: 'operador',
    ativo: true,
    gerar_senha_temporaria: true,
    enviar_email_configuracao: false
  });

  // Permissões
  const [permissions, setPermissions] = useState<Omit<UserPermissions, 'id' | 'usuario_id' | 'criado_em' | 'atualizado_em'>>(DEFAULT_PERMISSIONS);

  // Módulos ativos da empresa
  const [activeEmpresaModules, setActiveEmpresaModules] = useState<string[]>([]);

  // Conferente: filial e clientes
  const [filialId, setFilialId] = useState<number | null>(null);
  const [selectedClienteIds, setSelectedClienteIds] = useState<number[]>([]);
  const [filiais, setFiliais] = useState<{ id: number; razao_social: string; nome_fantasia?: string; cnpj: string }[]>([]);
  const [clientesDisponiveis, setClientesDisponiveis] = useState<{ id: number; razao_social: string; cnpj?: string }[]>([]);
  const [loadingFiliais, setLoadingFiliais] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);

  // Buscar módulos ativos da empresa ao montar
  useEffect(() => {
    listarModulosEmpresa()
      .then(res => {
        if (res.success && res.modulos) {
          setActiveEmpresaModules(
            res.modulos.filter(m => m.ativo).map(m => m.modulo)
          );
        }
      })
      .catch(() => {
        setActiveEmpresaModules([]);
      });
  }, []);

  // Carregar filiais e clientes quando tipo for conferente
  useEffect(() => {
    if (formData.tipo === 'conferente') {
      // Carregar filiais
      setLoadingFiliais(true);
      UserService.getFiliais()
        .then(res => {
          if (res.success) {
            setFiliais(res.filiais);
          }
        })
        .catch(() => setFiliais([]))
        .finally(() => setLoadingFiliais(false));

      // Carregar clientes
      setLoadingClientes(true);
      listarClientes({ limit: 200, ativo: 'true' })
        .then(res => {
          if (res.success && res.clientes) {
            setClientesDisponiveis(res.clientes.map(c => ({ id: c.id, razao_social: c.razao_social, cnpj: c.cnpj })));
          }
        })
        .catch(() => setClientesDisponiveis([]))
        .finally(() => setLoadingClientes(false));
    }
  }, [formData.tipo]);

  // Inicializar formulário com dados do usuário (modo edição)
  useEffect(() => {
    if (mode === 'edit' && user) {
      setFormData({
        nome: user.nome,
        email: user.email,
        telefone: user.telefone || '',
        cargo: user.cargo || '',
        tipo: user.tipo,
        ativo: user.ativo,
        gerar_senha_temporaria: false,
        enviar_email_configuracao: false
      });

      // Inicializar o tipo anterior com o tipo atual do usuário
      setPreviousTipo(user.tipo);

      if (user.permissoes) {
        setPermissions(user.permissoes);
      }

      // Carregar dados do conferente
      if (user.tipo === 'conferente') {
        setFilialId(user.filial_id || null);
        if (user.cliente_ids && user.cliente_ids.length > 0) {
          setSelectedClienteIds(user.cliente_ids);
        } else if (user.clientes_vinculados && user.clientes_vinculados.length > 0) {
          setSelectedClienteIds(user.clientes_vinculados.map(c => c.cliente_id));
        }
      }
    }
  }, [mode, user]);

  // Guardar o tipo anterior para detectar mudanças
  const [previousTipo, setPreviousTipo] = useState<'admin' | 'supervisor' | 'operador' | 'conferente'>(formData.tipo);

  // Atualizar permissões quando tipo mudar
  useEffect(() => {
    if (previousTipo !== formData.tipo) {
      if (formData.tipo === 'admin') {
        setPermissions(ADMIN_PERMISSIONS);
      } else if (formData.tipo === 'conferente') {
        setPermissions(CONFERENTE_PERMISSIONS);
      } else if (formData.tipo === 'supervisor') {
        setPermissions(DEFAULT_PERMISSIONS);
      } else {
        setPermissions(DEFAULT_PERMISSIONS);
      }
      setPreviousTipo(formData.tipo);
    }
  }, [formData.tipo, previousTipo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionsChange = (newPermissions: Omit<UserPermissions, 'id' | 'usuario_id' | 'criado_em' | 'atualizado_em'>) => {
    setPermissions(newPermissions);
  };

  const handleClienteToggle = (clienteId: number) => {
    setSelectedClienteIds(prev =>
      prev.includes(clienteId)
        ? prev.filter(id => id !== clienteId)
        : [...prev, clienteId]
    );
  };

  const validateForm = (): string | null => {
    if (!formData.nome.trim()) {
      return 'Nome é obrigatório';
    }

    if (!formData.email.trim()) {
      return 'Email é obrigatório';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return 'Email inválido';
    }

    if (mode === 'create' && !formData.gerar_senha_temporaria && !formData.enviar_email_configuracao) {
      return 'Escolha uma opção de senha (gerar temporária ou enviar email)';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === 'create') {
        const createData: CreateUserData = {
          nome: formData.nome.trim(),
          email: formData.email.trim().toLowerCase(),
          tipo: formData.tipo,
          telefone: formData.telefone.trim() || undefined,
          cargo: formData.cargo.trim() || undefined,
          empresa_id: 0,
          gerar_senha_temporaria: formData.gerar_senha_temporaria,
          enviar_email_configuracao: formData.enviar_email_configuracao,
          permissoes: permissions
        };

        // Adicionar dados de conferente
        if (formData.tipo === 'conferente') {
          createData.filial_id = filialId;
          createData.cliente_ids = selectedClienteIds;
        }

        const response = await UserService.createUser(createData);

        if (response.senha_temporaria_gerada) {
          setTemporaryPassword(response.senha_temporaria_gerada);
        } else {
          onSuccess('Usuário criado com sucesso');
        }
      } else {
        if (!user) return;

        const updateData: UpdateUserData = {
          nome: formData.nome.trim(),
          email: formData.email.trim().toLowerCase(),
          tipo: formData.tipo,
          telefone: formData.telefone.trim() || undefined,
          cargo: formData.cargo.trim() || undefined,
          ativo: formData.ativo,
          permissoes: permissions
        };

        // Adicionar dados de conferente
        if (formData.tipo === 'conferente') {
          updateData.filial_id = filialId;
          updateData.cliente_ids = selectedClienteIds;
        }

        await UserService.updateUser(user.id, updateData);
        onSuccess('Usuário atualizado com sucesso');
      }
    } catch (err: any) {
      setError(err.message || `Erro ao ${mode === 'create' ? 'criar' : 'atualizar'} usuário`);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = () => {
    if (temporaryPassword) {
      navigator.clipboard.writeText(temporaryPassword);
      alert('Senha copiada para a área de transferência!');
    }
  };

  const handleCloseAfterPasswordCopy = () => {
    onSuccess('Usuário criado com sucesso');
  };

  // Se senha temporária foi gerada, mostrar tela de confirmação
  if (temporaryPassword) {
    return (
      <ModalPortal isOpen={true}>
        <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-password-display" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>
              <i className="fas fa-check-circle" style={{ color: '#04C64F' }}></i> Usuário Criado
            </h2>
          </div>

          <div className="modal-body">
            <div className="password-display-container">
              <p className="password-display-warning">
                <i className="fas fa-exclamation-triangle"></i>
                <strong>Atenção:</strong> Guarde esta senha temporária. Ela não será exibida novamente!
              </p>

              <div className="password-display-box">
                <label>Senha Temporária:</label>
                <div className="password-display-value">
                  <code>{temporaryPassword}</code>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleCopyPassword}
                    title="Copiar senha"
                  >
                    <i className="fas fa-copy"></i> Copiar
                  </button>
                </div>
              </div>

              <p className="password-display-info">
                O usuário deverá alterar esta senha no primeiro acesso.
              </p>
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-primary"
              onClick={handleCloseAfterPasswordCopy}
            >
              Entendi, Fechar
            </button>
          </div>
        </div>
        </div>
      </ModalPortal>
    );
  }

  return (
    <ModalPortal isOpen={true}>
      <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className={`fas ${mode === 'create' ? 'fa-user-plus' : 'fa-user-edit'}`}></i>
            {mode === 'create' ? 'Novo Usuário' : 'Editar Usuário'}
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        <div className="modal-tabs">
          <button
            className={`modal-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <i className="fas fa-user"></i> Informações
          </button>
          <button
            className={`modal-tab ${activeTab === 'permissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('permissions')}
          >
            <i className="fas fa-shield-alt"></i> Permissões
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {activeTab === 'info' && (
              <div className="tab-content">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Nome Completo <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="nome"
                      className="form-control"
                      value={formData.nome}
                      onChange={handleInputChange}
                      placeholder="Digite o nome completo"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="usuario@exemplo.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Telefone</label>
                    <input
                      type="tel"
                      name="telefone"
                      className="form-control"
                      value={formData.telefone}
                      onChange={handleInputChange}
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Cargo/Função</label>
                    <input
                      type="text"
                      name="cargo"
                      className="form-control"
                      value={formData.cargo}
                      onChange={handleInputChange}
                      placeholder="Ex: Gerente, Operador, etc"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Tipo de Usuário <span className="required">*</span>
                    </label>
                    <select
                      name="tipo"
                      className="form-control"
                      value={formData.tipo}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="operador">Operador</option>
                      <option value="conferente">Conferente</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="admin">Administrador</option>
                    </select>
                    <small className="form-hint">
                      Ao alterar o tipo, as permissões serão preenchidas automaticamente, mas podem ser editadas na aba "Permissões"
                    </small>
                  </div>

                  {mode === 'edit' && (
                    <div className="form-group">
                      <label className="form-label">Status</label>
                      <div className="checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            name="ativo"
                            checked={formData.ativo}
                            onChange={handleInputChange}
                          />
                          <span>Usuário ativo</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Seção de Conferente: Filial e Clientes */}
                {formData.tipo === 'conferente' && (
                  <div className="form-section">
                    <h3 className="section-title">
                      <i className="fas fa-building" style={{ marginRight: 6 }}></i>
                      Configurações do Conferente
                    </h3>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Filial</label>
                        {loadingFiliais ? (
                          <div style={{ padding: '8px 0', color: '#7f8c8d', fontSize: '0.85rem' }}>
                            <i className="fas fa-spinner fa-spin"></i> Carregando filiais...
                          </div>
                        ) : filiais.length === 0 ? (
                          <div style={{ padding: '8px 0', color: '#7f8c8d', fontSize: '0.85rem' }}>
                            <i className="fas fa-info-circle"></i> Nenhuma filial cadastrada
                          </div>
                        ) : (
                          <select
                            className="form-control"
                            value={filialId || ''}
                            onChange={(e) => setFilialId(e.target.value ? parseInt(e.target.value) : null)}
                          >
                            <option value="">Selecione uma filial</option>
                            {filiais.map(f => (
                              <option key={f.id} value={f.id}>
                                {f.nome_fantasia || f.razao_social} ({f.cnpj})
                              </option>
                            ))}
                          </select>
                        )}
                        <small className="form-hint">
                          Filial onde este conferente está alocado
                        </small>
                      </div>
                    </div>

                    <div className="form-group" style={{ marginTop: 12 }}>
                      <label className="form-label">
                        Clientes Vinculados
                      </label>
                      {loadingClientes ? (
                        <div style={{ padding: '8px 0', color: '#7f8c8d', fontSize: '0.85rem' }}>
                          <i className="fas fa-spinner fa-spin"></i> Carregando clientes...
                        </div>
                      ) : clientesDisponiveis.length === 0 ? (
                        <div style={{ padding: '8px 0', color: '#7f8c8d', fontSize: '0.85rem' }}>
                          <i className="fas fa-info-circle"></i> Nenhum cliente cadastrado
                        </div>
                      ) : (
                        <div className="conferente-clientes-list" style={{
                          maxHeight: 200,
                          overflowY: 'auto',
                          border: '1px solid #ddd',
                          borderRadius: 8,
                          padding: 8
                        }}>
                          {clientesDisponiveis.map(cliente => (
                            <label
                              key={cliente.id}
                              className="checkbox-label"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 8px',
                                borderRadius: 6,
                                cursor: 'pointer',
                                backgroundColor: selectedClienteIds.includes(cliente.id) ? '#e8f5e9' : 'transparent',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selectedClienteIds.includes(cliente.id)}
                                onChange={() => handleClienteToggle(cliente.id)}
                                style={{ marginRight: 8 }}
                              />
                              <span style={{ flex: 1 }}>
                                <strong style={{ fontSize: '0.9rem' }}>{cliente.razao_social}</strong>
                                {cliente.cnpj && (
                                  <small style={{ display: 'block', color: '#7f8c8d', fontSize: '0.75rem' }}>
                                    CNPJ: {cliente.cnpj}
                                  </small>
                                )}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                      <small className="form-hint">
                        {selectedClienteIds.length === 0
                          ? 'Selecione os clientes onde este conferente fica alocado. Se nenhum for selecionado, todos os clientes serão exibidos.'
                          : selectedClienteIds.length === 1
                            ? '1 cliente selecionado - será selecionado automaticamente na página de conferência'
                            : `${selectedClienteIds.length} clientes selecionados - apenas estes serão exibidos na página de conferência`
                        }
                      </small>
                    </div>
                  </div>
                )}

                {mode === 'create' && (
                  <div className="form-section">
                    <h3 className="section-title">Configuração de Senha</h3>

                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="gerar_senha_temporaria"
                          checked={formData.gerar_senha_temporaria}
                          onChange={handleInputChange}
                        />
                        <span>
                          <strong>Gerar senha temporária</strong>
                          <small>A senha será exibida após a criação</small>
                        </span>
                      </label>

                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="enviar_email_configuracao"
                          checked={formData.enviar_email_configuracao}
                          onChange={handleInputChange}
                        />
                        <span>
                          <strong>Enviar email para o usuário criar sua própria senha</strong>
                          <small>Será enviado um link de configuração</small>
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'permissions' && (
              <div className="tab-content">
                <PermissionsEditor
                  permissions={permissions}
                  onChange={handlePermissionsChange}
                  activeEmpresaModules={activeEmpresaModules}
                />
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Salvando...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  {mode === 'create' ? 'Criar Usuário' : 'Salvar Alterações'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </ModalPortal>
  );
};

export default UserFormModal;
